import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "../api";
import { Baum } from "../komponenten/Baum";
import { ANSICHT_LABEL, type Ansicht } from "../komponenten/baum-layout";
import { useTitel, personTitel } from "../titel";

const ANSICHTEN: Ansicht[] = ["sanduhr", "vorfahren", "nachkommen", "umgebung"];

/**
 * Die gezeichnete Ahnentafel.
 *
 * Zwei Regler, die man auseinanderhalten muss:
 *   Ansicht — WELCHE Verwandten gezeigt werden (Linie oder ganze Umgebung)
 *   Umkreis — WIE WEIT die API überhaupt Daten liefert
 */
export default function StammbaumSeite() {
  const { id } = useParams();
  const personId = Number(id);
  const [grad, setGrad] = useState(4);
  const [modus, setModus] = useState<Ansicht>("sanduhr");

  const baum = useQuery({
    queryKey: ["baum", personId, grad],
    queryFn: () => api.stammbaum(personId, grad),
  });
  const detail = useQuery({ queryKey: ["person", personId], queryFn: () => api.person(personId) });

  const p = detail.data?.person;

  // „Baum:" davor, sonst steht die Person zweimal gleich im Verlauf -
  // einmal ihre Seite, einmal ihr Baum.
  useTitel(`Baum: ${personTitel(p, personId)}`);

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">
            Stammbaum {p ? `um ${p.vorname} ${p.famname}` : ""}
          </h1>
          <p className="text-sm text-black/50 dark:text-white/50">
            Ziehen verschiebt, Mausrad zoomt. Ein Klick auf einen Kasten macht
            diese Person zum Mittelpunkt, ein Doppelklick öffnet ihre Seite.
          </p>
        </div>

        <div className="flex flex-wrap items-end gap-4">
          <label className="flex flex-col text-sm">
            <span className="mb-1 text-black/60 dark:text-white/60">Ansicht</span>
            <select
              value={modus}
              onChange={(e) => setModus(e.target.value as Ansicht)}
              className="rounded-lg border border-black/15 bg-white px-2 py-1.5
                         dark:border-white/15 dark:bg-neutral-800"
            >
              {ANSICHTEN.map((a) => (
                <option key={a} value={a}>
                  {ANSICHT_LABEL[a]}
                </option>
              ))}
            </select>
          </label>

          <label className="flex flex-col text-sm">
            <span className="mb-1 text-black/60 dark:text-white/60">
              Umkreis {grad} · {baum.data?.personen.length ?? 0} geladen
            </span>
            <input
              type="range"
              min={1}
              max={6}
              value={grad}
              onChange={(e) => setGrad(Number(e.target.value))}
              className="w-40"
            />
          </label>
        </div>
      </div>

      {modus === "umgebung" && (
        <p className="mb-3 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-900">
          Diese Ansicht zeigt auch angeheiratete Zweige — Onkeln, Cousins und
          die Eltern der Schwägerinnen stehen dann in denselben Zeilen wie die
          eigene Linie. Aufschlussreich, aber schnell unübersichtlich.
        </p>
      )}

      {baum.error && <p className="text-red-700">{(baum.error as Error).message}</p>}
      {!baum.data && !baum.error && <p className="text-black/40">lädt …</p>}

      {baum.data &&
        (baum.data.personen.length <= 1 ? (
          <p className="rounded-xl border border-black/10 p-8 text-center text-black/50 dark:border-white/10">
            Für diese Person sind noch keine Verwandten eingetragen.
            <br />
            <Link to={`/person/${personId}`} className="underline">
              Auf der Personenseite ergänzen
            </Link>
          </p>
        ) : (
          <Baum daten={baum.data} modus={modus} umkreis={grad} />
        ))}

      {p && (
        <p className="mt-4 text-sm">
          <Link to={`/person/${personId}`} className="text-black/60 underline dark:text-white/60">
            ← zurück zu {p.vorname} {p.famname}
          </Link>
        </p>
      )}
    </div>
  );
}
